import { Box, Link, Stack, Theme } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid-pro';

import { ColorScale } from '@/theme/colors';
import { styled } from '@/theme/styled';
import { getBorder } from '@/theme/utils';

const EmptyStateContainer = styled(Stack)(({ theme }) => ({
  height: '100%',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
}));

const IconContainer = styled(Stack)(({ theme }) => ({
  background: theme.palette.background.subtle,
  border: getBorder(theme.palette.border.default),
  borderRadius: theme.shape.borderRadius,
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
  width: 28,
  height: 28,
}));

const CardNameContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(1),
  alignItems: 'center',
  maxWidth: `calc(100% - ${theme.spacing(4.5)})`,
}));

const ModelData = styled(Stack)(({ theme }) => ({
  background: theme.palette.background.default,
  transition: 'all 0.15s ease-in-out',
  flex: 1,
}));

const StatusContainer = styled(Stack)(({ theme }) => ({
  borderTop: getBorder(theme.palette.border.default),
  background: theme.palette.background.subtle,
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2.5),
  gap: theme.spacing(1),
}));

const StatusColors = styled(Stack)(({ theme }) => ({
  height: 3,
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1 / 8),
}));

const StatusColor = styled(Box)(({ theme }) => ({
  height: '100%',
}));

const ModelCard = styled(Stack)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: getBorder(theme.palette.border.default),
  boxShadow: theme.shadows[2],
  overflow: 'hidden',
  flex: 1,
  color: theme.palette.text.default,
  minHeight: 117,
  '&:hover': {
    border: getBorder(ColorScale.gray[300]),
    boxShadow: theme.shadows[3],
  },
}));

const ChangelogLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none !important',
  },
}));

ChangelogLink.defaultProps = {
  rel: 'noreferrer',
  underline: 'none',
};

const dataGridStyles = {
  border: 'none',
  boxShadow: 'none',
  [`& .${gridClasses.cell}`]: {
    borderBottom: 'none',
  },
  [`& .${gridClasses.columnHeaders}`]: {
    borderBottom: 'none',
    background: 'none',
  },
  [`& .${gridClasses.columnSeparator}`]: {
    display: 'none',
  },
  [`& .${gridClasses.columnHeader}:first-of-type .${gridClasses.columnHeaderTitle}`]: {
    paddingLeft: (theme: Theme) => theme.spacing(2),
  },
  [`& .${gridClasses.cell}:first-of-type`]: {
    paddingLeft: (theme: Theme) => theme.spacing(4),
  },
  [`& .${gridClasses.columnHeaderTitle}`]: {
    color: (theme: Theme) => theme.palette.text.muted, // Style the header text
  },
  [`& .${gridClasses.virtualScroller}`]: {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
  },
};

const dataGridProps = {
  hideFooter: true,
  pagination: false,
  scrollbarSize: 0,
};

const GridContainer = styled(Stack)(({ theme }) => ({
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
}));

export {
  EmptyStateContainer,
  IconContainer,
  CardNameContainer,
  StatusColors,
  StatusColor,
  ModelData,
  StatusContainer,
  ModelCard,
  ChangelogLink,
  dataGridStyles,
  dataGridProps,
  GridContainer,
};
